<template>
    <v-container fluid>
        <div align="center" justify="center" class="d-flex flex-row justify-center">
            <v-row no-gutters class="d-flex flex-column map-container">
                <v-container flat>
                    <v-btn class="ma-2" small color="attention" dark @click="restartApp"
                        >Restart
                        <v-icon dark right>mdi-delete</v-icon>
                    </v-btn>

                    <v-btn color="secondary" small dark @click="overlay = !overlay">
                        Upload CSV File
                        <v-icon dark right>mdi-upload</v-icon>
                    </v-btn>

                    <v-btn color="secondary" class="ma-2" small dark @click="createCSVFile"
                        >Download CSV File
                        <v-icon dark right>mdi-download</v-icon>
                    </v-btn>

                    <v-btn class="ma-2 ml-6" small color="primary" dark @click="addMapAndActivate"
                        >Add Map
                        <v-icon dark right>mdi-plus</v-icon>
                    </v-btn>
                </v-container>

                <v-overlay :value="overlay" opacity="0.95">
                    <v-btn icon @click="overlay = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>

                    <v-file-input
                        accept="text/csv"
                        placeholder="Upload Your Map Data"
                        prepend-icon="mdi-file-delimited"
                        label="Map Data"
                        @change="selectFile"
                    ></v-file-input>

                    <v-btn color="primary" dark small @click="upload(uploadFinished)">
                        Upload
                        <v-icon right dark>mdi-cloud-upload</v-icon>
                    </v-btn>
                </v-overlay>

                <v-container flat>
                    <input id="pac-input" class="search-box" type="text" placeholder="Search A Place To Center Map" />
                </v-container>
                <v-container
                    v-bind:style="{
                        height: getCurrentMap().height + 'px',
                        width: getCurrentMap().width + 'px',
                    }"
                    class="map mt-2 pa-0"
                >
                    <v-card class="d-flex map" flat tile>
                        <div id="map" class="map"></div>
                    </v-card>
                </v-container>
                <v-card class="d-flex pa-2 ma-2 controls" outlined tile>
                    <v-container class="grey lighten-5 pa-6">
                        <h2>
                            Define Your Map ({{ getCurrentMap().mapTitle }} –
                            {{ getCurrentMap().mapSet }})
                        </h2>
                        <v-select
                            :items="supportedLanguages"
                            v-model="getCurrentMap().language"
                            item-text="name"
                            item-value="code"
                            label="Language"
                            name="Language"
                            @change="changeLanguage()"
                        ></v-select>
                        <v-select
                            :items="mapStyles"
                            v-model="getCurrentMap().style"
                            item-text="name"
                            item-value="code"
                            label="Style"
                            name="MapStyle"
                            @change="changeStyle()"
                        ></v-select>
                        <v-subheader class="pa-0 mb-6 mt-6">Width / Height</v-subheader>
                        <v-slider
                            v-model="getCurrentMap().width"
                            :min="minSize"
                            :max="maxSize"
                            thumb-label="always"
                            label="px"
                            class="mt-4"
                            @change="updateMapProperties"
                        ></v-slider>

                        <v-slider
                            v-model="getCurrentMap().height"
                            :min="minSize"
                            :max="maxSize"
                            thumb-label="always"
                            label="px"
                            @change="updateMapProperties"
                        ></v-slider>

                        <v-switch
                            v-model="getCurrentMap().isScaled"
                            label="Double Resolution"
                            @change="changeScale()"
                        ></v-switch>

                        <v-btn @click="downloadImage()" color="primary mt-10">Download Image</v-btn>
                    </v-container>
                </v-card>
            </v-row>
            <v-container class="pa-2">
                <v-container flat>
                    <v-btn @click="openAllPanels" small class="ml-8">Open All</v-btn>
                    <v-btn @click="closeAllPanels" small class="ml-4">Close All</v-btn>
                </v-container>
                <v-container fluid class="map-panels">
                    <v-expansion-panels
                        :flat="true"
                        :tile="true"
                        v-model="mapPanel"
                        :focusable="true"
                        :multiple="true"
                        :popout="true"
                        :hover="true"
                    >
                        <draggable
                            v-model="imageList"
                            @end="createPanelList"
                            class="v-item-group theme--light v-expansion-panels v-expansion-panels--hover v-expansion-panels--popout"
                        >
                            <v-expansion-panel
                                v-for="(item, index) in imageList"
                                :key="item.i"
                                v-bind:class="{ 'current-panel': item.id === activePanelID }"
                                @change="updatePanelStatus(item.id)"
                            >
                                <v-expansion-panel-header>
                                    <div>
                                        <v-icon>mdi-drag</v-icon>
                                        <span class="pl-4"
                                            >{{ imageList[index].mapTitle }} ({{
                                                imageList[index].mapSet
                                            }}.{{ imageList[index].id }}) –
                                            {{ imageList[index].height * imageList[index].scale }}
                                            x
                                            {{ imageList[index].width * imageList[index].scale }}px
                                            |

                                            {{ imageList[index].style }} |
                                            {{ imageList[index].language }}
                                        </span>
                                    </div>
                                </v-expansion-panel-header>
                                <!-- <v-virtual-scroll :items="items" :item-height="50" height="300"> -->
                                <v-expansion-panel-content>
                                    <!-- GRID LAYOUT -->
                                    <v-container>
                                        <v-row no-gutters>
                                            <v-col sm3 cols="3">
                                                <v-img
                                                    :src="imageList[index].thumbnail"
                                                    aspect-ratio="1.5"
                                                ></v-img>
                                            </v-col>
                                            <v-col sm3 cols="9">
                                                <!-- Custom Info-->
                                                <v-row no-gutters>
                                                    <v-text-field
                                                        class="ma-2 pl-2"
                                                        label="Title"
                                                        v-model="imageList[index].mapTitle"
                                                        :disabled="item.id !== activePanelID"
                                                        @keyup="saveToLocalStorage()"
                                                    ></v-text-field>
                                                    <v-text-field
                                                        class="ma-2 pl-2"
                                                        label="MapSet (ID)"
                                                        v-model="imageList[index].mapSet"
                                                        :disabled="item.id !== activePanelID"
                                                        @keyup="saveToLocalStorage()"
                                                    ></v-text-field>
                                                </v-row>
                                                <v-row no-gutters>
                                                    <v-textarea
                                                        v-model="imageList[index].mapDescription"
                                                        :disabled="item.id !== activePanelID"
                                                        class="ma-2 pl-2"
                                                        label="Description"
                                                        model="imageList[index].mapDescription"
                                                        hint="Optional text for map description"
                                                        height="80"
                                                        @keyup="saveToLocalStorage()"
                                                    ></v-textarea>
                                                </v-row>

                                                <!-- Map INFO -->
                                                <v-row no-gutters>
                                                    <v-text-field
                                                        :disabled="item.id !== activePanelID"
                                                        class="ma-2 pl-2"
                                                        label="Zoom"
                                                        v-model="imageList[index].mapZoom"
                                                        @change="
                                                            changeMapPosition({
                                                                latitude: imageList[index].latitude,
                                                                longitude:
                                                                    imageList[index].longitude,
                                                                mapZoom: imageList[index].mapZoom,
                                                            })
                                                        "
                                                    ></v-text-field>
                                                    <v-text-field
                                                        :disabled="item.id !== activePanelID"
                                                        class="ma-2 pl-2"
                                                        label="Latitude"
                                                        v-model="imageList[index].latitude"
                                                        @change="
                                                            changeMapPosition({
                                                                latitude: imageList[index].latitude,
                                                                longitude:
                                                                    imageList[index].longitude,
                                                                mapZoom: imageList[index].mapZoom,
                                                            })
                                                        "
                                                    ></v-text-field>
                                                    <v-text-field
                                                        :disabled="item.id !== activePanelID"
                                                        class="ma-2 pl-2"
                                                        label="Longitude"
                                                        v-model="imageList[index].longitude"
                                                        @change="
                                                            changeMapPosition({
                                                                latitude: imageList[index].latitude,
                                                                longitude:
                                                                    imageList[index].longitude,
                                                                mapZoom: imageList[index].mapZoom,
                                                            })
                                                        "
                                                    ></v-text-field>
                                                </v-row>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                    <!-- GRID LAYOUT END -->
                                    <v-btn
                                        class="ma-2"
                                        small
                                        color="primary"
                                        :disabled="item.id === activePanelID"
                                        @click="modifyMap(item.id)"
                                        >Modify Map
                                        <v-icon dark right>mdi-pencil</v-icon>
                                    </v-btn>

                                    <v-btn
                                        class="ma-2"
                                        small
                                        color="primary"
                                        :disabled="item.id !== activePanelID"
                                        @click="saveMap(item.id)"
                                        >Preview Map
                                        <v-icon dark right>mdi-check-bold</v-icon>
                                    </v-btn>

                                    <v-btn
                                        class="ma-2"
                                        small
                                        color="attention"
                                        dark
                                        @click="removeMapAt(item.id)"
                                        >Remove Map
                                        <v-icon dark right>mdi-minus-circle</v-icon>
                                    </v-btn>
                                </v-expansion-panel-content>
                                <!-- </v-virtual-scroll> -->
                            </v-expansion-panel>
                        </draggable>
                    </v-expansion-panels>
                </v-container>
            </v-container>
        </div>
    </v-container>
</template>

<script>
import gmapsInit from '../js/gmaps';
import Papa from 'papaparse';
import { styleToParams } from '../js/styleToParams';
import mapStyleDefinitions from '../assets/mapAssets/mapStyles';
import config from '../js/config';

let imageID = 0;
import draggable from 'vuedraggable';

export default {
    name: 'MapCreator',
    display: 'Static Map Creator',
    instruction: 'Interface to create map storied',
    components: {
        draggable,
    },
    created() {
        this.init(); // Add first map block + local storage
    },

    async mounted() {},
    data: () => ({
        /* 2 variables store in local storage */
        imageList: [], // List of all maps in app
        activePanelID: null, // This map is currently edited

        /* Other data */
        mapPanel: [], // Array of open and closed panels
        loadFromMemory: false, // Will be set to true if localstorage was found
        overlay: false,

        uploadFile: null,
        csvFile: null,

        mapStyles: [],
        supportedLanguages: [
            {
                name: 'English',
                code: 'en',
            },
            {
                name: 'French',
                code: 'fr',
            },
            {
                name: 'German',
                code: 'de',
            },
            {
                name: 'Spanish',
                code: 'es',
            },
            {
                name: 'Portuguese',
                code: 'pt',
            },
        ],
        map: null,
        // These properties are default values if no local storage is available
        mapProperties: {
            id: 1,
            mapSet: 1,
            panelStatus: 0,
            style: 'default',
            width: 640,
            height: 360,
            mapZoom: 3,
            latitude: 52.540836995172214,
            longitude: 10.322207864471071,
            isScaled: true,
            scale: 2,
            language: 'en',
            mapTitle: 'New Map',
            mapDescription: '',
            thumbnail: '/assets/img/placeholder-image.png',
            elementID: 'map',
        },
        imageFormat: 'png',
        minSize: 40,
        maxSize: 640,
    }),
    computed: {
        draggingInfo() {
            return this.dragging ? 'under drag' : '';
        },
    },
    methods: {
        upload(callback) {
            const reader = new FileReader();

            reader.addEventListener(
                'load',
                function() {
                    this.csvFile = reader.result;
                    callback(this.csvFile);
                }.bind(this),
                false
            );

            reader.readAsText(this.uploadFile);
        },
        uploadFinished(csvString) {
            const JSON = Papa.parse(csvString, {
                header: true,
                dynamicTyping: true,
            });
            this.imageList = JSON.data;

            if (this.imageList.length === 0) {
                // Image List is Empty - create one to start with
                this.activePanelID = this.addMapAndActivate();
            }

            this.overlay = false;

            // make first item active
            this.activePanelID = this.imageList[0].id;
            this.createPanelList();
            this.modifyMap(this.activePanelID);

            this.saveToLocalStorage();
        },
        selectFile(file) {
            this.uploadFile = file;
        },

        restartApp() {
            this.imageList = [];
            this.activePanelID = this.addMapAndActivate();
            this.saveToLocalStorage();
        },
        init() {
            this.createMapStyles();
            // Use local storage only if all values are present
            if (localStorage.getItem('imageList') && localStorage.activePanelID) {
                try {
                    this.imageList = JSON.parse(localStorage.getItem('imageList'));

                    if (this.imageList.length === 0) {
                        // Image List is Empty - create one to start with
                        this.activePanelID = this.addMapAndActivate();
                    } else {
                        this.activePanelID = localStorage.activePanelID;
                        this.createPanelList();
                        this.loadFromMemory = true;

                        /* Find Highest ID */
                        imageID = Math.max.apply(
                            Math,
                            this.imageList.map(function(o) {
                                return o.id;
                            })
                        );
                        // Activate map
                        this.modifyMap(this.activePanelID);
                    }
                } catch (e) {
                    localStorage.removeItem('imageList');
                    localStorage.removeItem('activePanelID');

                    // Just create a new one
                    this.activePanelID = this.addMapAndActivate();
                }
            } else {
                this.activePanelID = this.addMapAndActivate();
            }
        },

        createMapStyles() {
            Object.keys(mapStyleDefinitions).forEach((key) => {
                const obj = {};
                obj.code = key;
                obj.name = mapStyleDefinitions[key].name;

                this.mapStyles.push(obj);
            });
        },

        compare(a, b) {
            // Use toUpperCase() to ignore character casing
            const nameA = a.id;
            const nameB = b.id;

            let comparison = 0;
            if (nameA > nameB) {
                comparison = 1;
            } else if (nameA < nameB) {
                comparison = -1;
            }
            return comparison;
        },

        async createCSVFile() {
            // ** Download images for each map in image List
            const createBlobArray = async () => {
                const all = Promise.all(
                    this.imageList.map(async (map) => {
                        /* Make sure all required parameters are available */
                        if (!map.latitude) {
                            map.latitude = 0;
                        }
                        if (!map.longitude) {
                            map.longitude = 0;
                        }
                        if (!map.language) {
                            map.language = 'en';
                        }
                        if (!map.width) {
                            map.width = 300;
                        }
                        if (!map.height) {
                            map.height = 200;
                        }
                        if (!map.scale) {
                            map.scale = 2;
                        }
                        if (!map.mapZoom) {
                            map.mapZoom = 10;
                        }
                        /* END – Make sure all required parameters are available */

                        const imgSrc = this.createMapURL({
                            thumbnail: false,
                            mapProps: map,
                        });

                        const response = await fetch(imgSrc);
                        const data = await response.blob();
                        const obj = {};
                        obj.id = map.id;
                        obj.data = data;
                        return obj;
                    })
                );
                return await all;
            };
            const blobArray = await createBlobArray();

            // ** Convert array to base64
            const createBase64 = async (callback) => {
                const all = Promise.all(
                    blobArray.map(async (blob) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            const obj = {};
                            obj.id = blob.id;
                            obj.data = reader.result;
                            callback(obj);
                            // return
                        };
                        reader.readAsDataURL(blob.data);
                    })
                );
                return await all;
            };

            let readyImages = 0;
            const b64Array = [];
            const checkImageCompletion = (imgObj) => {
                readyImages++;
                b64Array.push(imgObj);
                if (readyImages === this.imageList.length) {
                    sendCSV();
                }
            };

            /* Execute IMG conversion, wait for callback */
            await createBase64((img) => {
                checkImageCompletion(img);
            });

            const sendCSV = () => {
                /*
                CREATE LATLONG field of center */
                this.imageList.map((map) => {
                    map.mapCenter = `${map.latitude},${map.longitude}`;
                });

                // Attach b64 to json
                this.imageList.map((map) => {
                    const id = map.id;
                    const imgData = b64Array.find((item) => {
                        return Number(item.id) === id;
                    });
                    map.mapData = imgData.data;
                });

                // convert to csv
                const csvFile = Papa.unparse(this.imageList, {
                    quotes: false, //or array of booleans
                    quoteChar: '"',
                    escapeChar: '"',
                    delimiter: ',',
                    header: true,
                    newline: '\r\n',
                    skipEmptyLines: false, //other option is 'greedy', meaning skip delimiters, quotes, and whitespace.
                    columns: null, //or array of strings
                });

                const csvBlob = new Blob([csvFile], { type: 'text/csv;charset=utf-8;' });

                this.downloadFile({
                    file: csvBlob,
                    fileName: 'map-set.csv',
                });
            };
        },

        updatePanelStatus(mapId) {
            /* Panel is opened manually >> update status in list */
            const currentMap = this.imageList.find((item) => {
                return Number(item.id) === Number(mapId);
            });
            currentMap.panelStatus = currentMap.panelStatus === 1 ? 0 : 1;

            this.saveToLocalStorage();
        },

        createPanelList() {
            /* Panel is dragged manually >> update panel list based on imageList */
            this.mapPanel = [];
            this.imageList.map((panel, index) => {
                if (panel.panelStatus) {
                    this.mapPanel.push(index);
                }
            });
            this.saveToLocalStorage();
        },

        removeMapAt(idx) {
            this.imageList.splice(
                this.imageList.findIndex((item) => item.id === idx),
                1
            );
            this.createPanelList();

            // If remove panel was active panel >> first panel becomes active
            if (idx == this.activePanelID) {
                if (this.imageList.length > 0) {
                    this.activePanelID = this.imageList[0].id;
                } else {
                    this.activePanelID = null;
                }
            }
            this.saveToLocalStorage();
        },
        addMap: function() {
            let baseMap = {};
            if (!this.activePanelID) {
                Object.keys(this.mapProperties).forEach((key) => {
                    baseMap[key] = this.mapProperties[key];
                });
            } else {
                const currentMap = this.getCurrentMap();
                Object.keys(currentMap).forEach((key) => {
                    baseMap[key] = currentMap[key];
                });
            }
            imageID++;
            baseMap.id = imageID;

            this.imageList.push(baseMap);

            this.saveToLocalStorage();

            return imageID;
        },
        getCurrentMap() {
            if (this.imageList.length === 0) {
                return this.mapProperties;
            } else {
                const props = this.imageList.find((item) => {
                    return Number(item.id) === Number(this.activePanelID);
                });
                return props;
            }
        },

        addMapAndActivate() {
            // Close current panel
            const mapPosition = this.imageList.findIndex((item) => item.id === this.activePanelID);
            var indexInMapPanel = this.mapPanel.indexOf(mapPosition);
            this.mapPanel.splice(indexInMapPanel, 1);
            this.getCurrentMap().panelStatus = 0;

            const newPanelID = this.addMap();
            const currentMap = this.modifyMap(newPanelID);

            currentMap.panelStatus = 1;
            this.mapPanel.push(this.imageList.length - 1);

            this.saveToLocalStorage();

            return newPanelID;
        },

        modifyMap(id) {
            this.activePanelID = Number(id);
            const currentMap = this.imageList.find((item) => {
                return item.id === Number(id);
            });

            this.loadMap(currentMap);
            this.saveToLocalStorage();

            return currentMap;
        },

        saveMap(id) {
            const imgNum = this.imageList.findIndex((item) => item.id === id);
            const imgSrc = this.createMapURL({
                thumbnail: this,
                mapProps: this.getCurrentMap(),
            });
            this.imageList[imgNum].thumbnail = imgSrc;

            this.saveToLocalStorage();
        },

        saveToLocalStorage() {
            const parsedImageList = JSON.stringify(this.imageList);
            localStorage.setItem('imageList', parsedImageList);

            localStorage.setItem('activePanelID', this.activePanelID);
        },

        updateMapProperties() {
            const imgNum = this.imageList.findIndex((item) => item.id === this.activePanelID);
            this.imageList[imgNum].latitude = this.getCurrentMap().latitude;
            this.imageList[imgNum].longitude = this.getCurrentMap().longitude;
            this.imageList[imgNum].mapZoom = this.getCurrentMap().mapZoom;

            this.imageList[imgNum].scale = this.getCurrentMap().scale;
            this.imageList[imgNum].height = this.getCurrentMap().height;
            this.imageList[imgNum].width = this.getCurrentMap().width;
            this.imageList[imgNum].style = this.getCurrentMap().style;
            this.imageList[imgNum].language = this.getCurrentMap().language;

            this.saveToLocalStorage();
        },
        openAllPanels() {
            const panelNum = this.imageList.length;
            this.mapPanel = [...Array(panelNum).keys()].map((k, i) => i);
            this.saveToLocalStorage();
        },
        // Reset the panel
        closeAllPanels() {
            this.mapPanel = [];
            this.saveToLocalStorage();
        },

        async loadMap(props, reload = false) {
            try {
                const google = await gmapsInit(
                    {
                        language: this.getCurrentMap().language,
                    },
                    reload
                );
                /* Create Google Map */
                const mapEL = document.getElementById(props.elementID);
                this.map = new google.maps.Map(mapEL, {
                    streetViewControl: false,
                    mapTypeControl: false,
                    language: this.getCurrentMap().language,
                });
                this.changeMapPosition({
                    latitude: props.latitude,
                    longitude: props.longitude,
                    mapZoom: props.mapZoom,
                });
                this.map.setOptions({ styles: mapStyleDefinitions[props.style].style });

                /* Events */
                this.map.center_changed = () => {
                    this.setMapProps(this.map);
                };

                this.map.zoom_changed = () => {
                    this.setMapProps(this.map);
                };

                /* Add Search Box */
                const inputEl = document.getElementById('pac-input');
                this.searchBox = new google.maps.places.Autocomplete(inputEl);

                // Listen for the event fired
                this.searchBox.addListener('place_changed', () => {
                    const places = this.searchBox.getPlace();

                    const mapProps = {}
                    mapProps.latitude =  places.geometry.location.lat()
                    mapProps.longitude =  places.geometry.location.lng()
                    mapProps.mapZoom = this.getCurrentMap().mapZoom


                    if (places.length == 0) {
                        return;
                    } else {
                        this.changeMapPosition(mapProps)
                    }
                });


            } catch (error) {
                console.error(error);
            }
        },
        changeMapPosition(props) {
            this.map.setCenter({ lat: Number(props.latitude), lng: Number(props.longitude) });
            this.map.setZoom(Number(props.mapZoom));
        },
        changeStyle() {
            this.loadMap(this.getCurrentMap());
            this.updateMapProperties();
        },
        changeLanguage() {
            this.updateMapProperties();
            // TO COME - must reload window
        },
        setMapProps(map) {
            const newCenter = map.getCenter();
            this.getCurrentMap().latitude = newCenter.lat();
            this.getCurrentMap().longitude = newCenter.lng();
            this.getCurrentMap().mapZoom = map.getZoom();

            this.updateMapProperties();
        },
        changeScale() {
            this.getCurrentMap().scale = this.getCurrentMap().isScaled === true ? 2 : 1;
            this.updateMapProperties();
        },
        downloadImage() {
            const url = this.createMapURL({
                thumbnail: false,
                mapProps: this.getCurrentMap(),
            });
            this.createBlobAndDownload({
                url,
                mimeType: `image/${this.imageFormat}`,
                fileName: `${this.getCurrentMap().mapZoom}_${this.getCurrentMap().latitude}_${
                    this.getCurrentMap().longitude
                }.${this.imageFormat}`,
            });
        },
        createMapURL(params) {
            /*
             * @thumbnail: produce only small thumbnail
             * @mapProps: object with map properties
             */
            const styles = styleToParams.parse(mapStyleDefinitions[params.mapProps.style].style);
            const styleParams = styleToParams.get(styles);

            let staticUrl = config.STATIC_URL + styleParams;
            if (params.thumbnail) {
                staticUrl += `&size=${300}x${200}`;
            } else {
                staticUrl += `&size=${params.mapProps.width}x${params.mapProps.height}`;
            }
            staticUrl += `&center=${params.mapProps.latitude},${params.mapProps.longitude}`;
            staticUrl += `&scale=${params.mapProps.scale}`;
            staticUrl += `&zoom=${params.mapProps.mapZoom}`;
            staticUrl += `&format=${this.imageFormat}`;
            staticUrl += `&language=${params.mapProps.language}`;
            staticUrl += `&key=${config.MAPS_APIKEY}`;

            return staticUrl;
        },

        async createBlobAndDownload(props) {
            /*
             * @url: URL to fetch
             * @mimeType : complete mime type (image/png)
             * @fileName: filename with extension
             */
            const response = await fetch(props.url);
            const data = await response.blob();
            const metadata = {
                type: props.mimeType,
            };
            const file = new File([data], props.fileName, metadata);
            this.downloadFile({
                file,
                fileName: props.fileName,
            });
        },

        downloadFile(props) {
            /*
             * @file: blob to be downloaded
             * @fileName: filename with extension
             */
            const fileURL = URL.createObjectURL(props.file);
            const downloadLink = document.createElement('a');

            downloadLink.href = fileURL;
            downloadLink.download = props.fileName;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        },
    },
};
</script>

<style lang="scss">
.map {
    width: 100%;
    height: 100%;

    span {
        color: black;
    }
}

.search-box {
    width: 100%;
    padding: 0 0 3px;
    border-bottom: 1px solid var(--v-grey-base);

    &:focus {
        outline: none;
    }
}

.controls {
    width: 100%;
}

.map-panels {
    overflow: scroll;
    max-height: calc(100vh - 175px);
}

.map-container {
    min-width: 660px;
}

h2,
h3 {
    margin-bottom: 15px;
}
</style>

<style scoped lang="scss">
.current-panel {
    // border-top: 2px solid var(--v-primary-base);

    .v-expansion-panel-header {
        background-color: var(--v-primary-base) !important;
    }
}

.v-expansion-panel {
    border-radius: 0 !important;
}
</style>
